import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useI18n } from "@/context/I18nContext";
import { ArrowRight } from "lucide-react";
import { CERTIFICATION_FIELDS, SERVICE_TYPES, SITE_STATUS_ACTION_OPTIONS, parseDealerCodes } from "./actions";

const ACTION_OPTIONS = ["ddmMigration", "certification", "service", "cookieConsent", "siteStatus"];

function isComplete(action, params) {
  if (!action) return false;
  if (action === "ddmMigration") return typeof params.setTo === "boolean";
  if (action === "certification") return !!params.field && typeof params.setTo === "boolean";
  if (action === "service") return !!params.serviceType && !!params.mode;
  if (action === "cookieConsent") return !!params.direction;
  if (action === "siteStatus") return !!params.action;
  return false;
}

export function SelectStep({
  action,
  setAction,
  params,
  setParams,
  dealerCodesText,
  setDealerCodesText,
  loadingDealers,
  onNext,
  canManageCookieConsents = false,
}) {
  const { t } = useI18n();
  // Same permission gate as CookieConsentsCard's buttons and Settings ->
  // OneTrust Cookie Consent — hide the option rather than let someone pick it
  // and only find out it's forbidden at the review/submit step.
  const visibleActionOptions = ACTION_OPTIONS.filter((opt) => opt !== "cookieConsent" || canManageCookieConsents);

  const selectAction = (nextAction) => {
    setAction(nextAction);
    const defaults =
      nextAction === "ddmMigration" || nextAction === "certification"
        ? { setTo: true }
        : nextAction === "service"
          ? { mode: "add" }
          : {};
    setParams(defaults);
  };

  const codeCount = parseDealerCodes(dealerCodesText).length;
  const canProceed = isComplete(action, params) && codeCount > 0 && !loadingDealers;

  return (
    <div className="flex flex-col gap-6">
      <Card className="p-6" data-testid="bulk-update-action-card">
        <h2 className="mb-4 text-lg font-semibold">{t("bulkUpdate.actionCardTitle")}</h2>
        <RadioGroup
          value={action || ""}
          onValueChange={selectAction}
          className="gap-4"
          data-testid="bulk-update-action-radio"
        >
          {visibleActionOptions.map((opt) => (
            <div key={opt} className="rounded-md border border-border p-4">
              <label
                className="flex cursor-pointer items-center gap-2 text-sm font-medium"
                data-testid={`bulk-update-action-${opt}`}
              >
                <RadioGroupItem value={opt} id={`bulk-update-action-radio-${opt}`} />
                {t(`bulkUpdate.actions.${opt}.label`)}
              </label>

              {action === opt && (
                <div className="mt-4 flex flex-wrap items-end gap-6 pl-6">
                  {opt === "ddmMigration" && (
                    <div>
                      <Label className="data-label mb-1 block">{t("bulkUpdate.setTo.label")}</Label>
                      <RadioGroup
                        value={params.setTo === false ? "false" : "true"}
                        onValueChange={(v) => setParams({ ...params, setTo: v === "true" })}
                        className="flex gap-4"
                        data-testid="bulk-update-ddm-migration-setto"
                      >
                        <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                          <RadioGroupItem value="true" />
                          {t("bulkUpdate.setTo.true")}
                        </label>
                        <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                          <RadioGroupItem value="false" />
                          {t("bulkUpdate.setTo.false")}
                        </label>
                      </RadioGroup>
                    </div>
                  )}

                  {opt === "certification" && (
                    <>
                      <div className="w-56">
                        <Label className="data-label mb-1 block">
                          {t("bulkUpdate.actions.certification.fieldLabel")}
                        </Label>
                        <Select value={params.field || ""} onValueChange={(v) => setParams({ ...params, field: v })}>
                          <SelectTrigger data-testid="bulk-update-certification-field">
                            <SelectValue placeholder={t("legal.select")} />
                          </SelectTrigger>
                          <SelectContent>
                            {CERTIFICATION_FIELDS.map((f) => (
                              <SelectItem key={f} value={f}>
                                {t(`bulkUpdate.fields.${f}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="data-label mb-1 block">{t("bulkUpdate.setTo.label")}</Label>
                        <RadioGroup
                          value={params.setTo === false ? "false" : "true"}
                          onValueChange={(v) => setParams({ ...params, setTo: v === "true" })}
                          className="flex gap-4"
                          data-testid="bulk-update-certification-setto"
                        >
                          <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                            <RadioGroupItem value="true" />
                            {t("bulkUpdate.setTo.true")}
                          </label>
                          <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                            <RadioGroupItem value="false" />
                            {t("bulkUpdate.setTo.false")}
                          </label>
                        </RadioGroup>
                      </div>
                    </>
                  )}

                  {opt === "service" && (
                    <>
                      <div className="w-56">
                        <Label className="data-label mb-1 block">{t("bulkUpdate.actions.service.fieldLabel")}</Label>
                        <Select
                          value={params.serviceType || ""}
                          onValueChange={(v) => setParams({ ...params, serviceType: v })}
                        >
                          <SelectTrigger data-testid="bulk-update-service-type">
                            <SelectValue placeholder={t("legal.select")} />
                          </SelectTrigger>
                          <SelectContent>
                            {SERVICE_TYPES.map((s) => (
                              <SelectItem key={s} value={s}>
                                {t(`serviceTypes.${s}`)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="data-label mb-1 block">{t("bulkUpdate.actions.service.modeLabel")}</Label>
                        <RadioGroup
                          value={params.mode || "add"}
                          onValueChange={(v) => setParams({ ...params, mode: v })}
                          className="flex gap-4"
                          data-testid="bulk-update-service-mode"
                        >
                          <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                            <RadioGroupItem value="add" />
                            {t("bulkUpdate.actions.service.add")}
                          </label>
                          <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                            <RadioGroupItem value="remove" />
                            {t("bulkUpdate.actions.service.remove")}
                          </label>
                        </RadioGroup>
                      </div>
                    </>
                  )}

                  {opt === "cookieConsent" && (
                    <div>
                      <Label className="data-label mb-1 block">
                        {t("bulkUpdate.actions.cookieConsent.directionLabel")}
                      </Label>
                      <RadioGroup
                        value={params.direction || ""}
                        onValueChange={(v) => setParams({ ...params, direction: v })}
                        className="flex gap-4"
                        data-testid="bulk-update-cookie-direction"
                      >
                        <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                          <RadioGroupItem value="add" />
                          {t("bulkUpdate.actions.cookieConsent.add")}
                        </label>
                        <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                          <RadioGroupItem value="remove" />
                          {t("bulkUpdate.actions.cookieConsent.remove")}
                        </label>
                      </RadioGroup>
                    </div>
                  )}

                  {opt === "siteStatus" && (
                    <div className="w-64">
                      <Label className="data-label mb-1 block">{t("bulkUpdate.actions.siteStatus.fieldLabel")}</Label>
                      <Select value={params.action || ""} onValueChange={(v) => setParams({ ...params, action: v })}>
                        <SelectTrigger data-testid="bulk-update-site-status-action">
                          <SelectValue placeholder={t("legal.select")} />
                        </SelectTrigger>
                        <SelectContent>
                          {SITE_STATUS_ACTION_OPTIONS.map((a) => (
                            <SelectItem key={a} value={a}>
                              {t(`dealership.siteStatusActions.${a}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </RadioGroup>
      </Card>

      <Card className="p-6">
        <Label className="data-label mb-1 block">{t("bulkUpdate.dealerCodes.label")}</Label>
        <p className="mb-2 text-xs text-muted-foreground">{t("bulkUpdate.dealerCodes.hint")}</p>
        <Textarea
          data-testid="bulk-update-dealer-codes"
          rows={8}
          value={dealerCodesText}
          onChange={(e) => setDealerCodesText(e.target.value)}
          placeholder={t("bulkUpdate.dealerCodes.placeholder")}
          className="font-mono text-xs"
        />
      </Card>

      <div>
        <Button data-testid="bulk-update-next" disabled={!canProceed} onClick={onNext} className="gap-1.5">
          {t("bulkUpdate.next")}
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
